/**
 * @module languages
 */
import { createReducer } from '@reduxjs/toolkit';
import { metaActions } from '~/behaviors/actions/meta.actions';
import { languageActions } from '~/behaviors/actions/language.actions';
import { accountActions } from '~/behaviors/actions/account.actions';

const defaultState = {
  codeIds: {}, // A convenience map of ISO codes => database IDs
};

const reset = () => defaultState;

/**
 * The action creator for languages will provide 2 types of data:
 *
 * 1. the normalized `entities` which is the regular dict of ids => data
 * 2. a dict that maps lang ISO codes => ids (for quick reverse reference to id by iso code)
 *
 * The resulting entity will have a map of each language id => data + a special
 * attribute `codeIds` which maps each ISO code to its corresponding database id.
 *
 * @example input action
 * action.payload = {
 *   entities: {
 *     languages: {
 *       1: {
 *         id: 1,
 *         code: 'en-ca'
 *       },
 *       2: {
 *         id: 2,
 *         code: 'fr-ca'
 *       }
 *     }
 *   },
 *   languageCodeIds: {
 *     'en-ca': 1,
 *     'fr-ca': 2
 *   }
 * }
 *
 * @example output state
 * entities.languages = {
 *   1: {
 *     id: 1,
 *     code: 'en-ca'
 *   },
 *   2: {
 *     id: 2,
 *     code: 'fr-ca'
 *   },
 *   codeIds: {
 *     'en-ca': 1,
 *     'fr-ca': 2
 *   }
 * }
 */
const mergeLanguages = (state, action) => {
  const languages = action?.payload?.entities?.languages || {};
  const languageCodeIds = action?.payload?.languageCodeIds || {};

  // Update each individual `entity`.
  Object.values(languages).forEach((language) => {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    state[language.id] = {
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      ...state[language.id],
      // @ts-expect-error TS(2698): Spread types may only be created from object types... Remove this comment to see the full error message
      ...language,
    };
  });

  // Update each individual code->id map.
  Object.keys(languageCodeIds).forEach((code) => {
    state.codeIds[code] = languageCodeIds[code];
  });
};

const languages = createReducer(defaultState, (builder) => {
  builder.addCase(metaActions.reset, reset);
  builder.addCase(accountActions.logout.pending, reset);
  builder.addCase(languageActions.fetchAllLanguages.fulfilled, mergeLanguages);
});

export default languages;
